import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, PencilSimple, Trash, MagnifyingGlass, X, CaretUp, CaretDown, CaretUpDown } from "@phosphor-icons/react";
import { SkeletonRows } from "@/components/RegisterSkeleton";

function TagsInput({ value, onChange, testid }) {
  const [draft, setDraft] = useState("");
  const tags = Array.isArray(value) ? value : [];
  const commit = (raw) => {
    const parts = raw.split(",").map((s) => s.trim()).filter(Boolean);
    if (parts.length) onChange([...tags, ...parts.filter((p) => !tags.includes(p))]);
    setDraft("");
  };
  return (
    <div className="w-full min-h-9 px-1.5 py-1 border border-gray-300 rounded-none flex flex-wrap gap-1 items-center bg-white focus-within:ring-2 focus-within:ring-equipr-yellow" data-testid={testid}>
      {tags.map((t) => (
        <span key={t} className="inline-flex items-center gap-1 bg-equipr-yellow/25 border border-equipr-yellow text-equipr-black text-xs font-semibold px-2 py-0.5" data-testid={`tag-pill-${t}`}>
          {t}
          <button type="button" onClick={() => onChange(tags.filter((x) => x !== t))} className="hover:text-red-600 leading-none font-bold">×</button>
        </span>
      ))}
      <input className="flex-1 min-w-[100px] h-6 text-sm outline-none border-none bg-transparent"
        value={draft}
        placeholder={tags.length ? "" : "Type, then comma or Enter…"}
        onChange={(e) => { const v = e.target.value; v.includes(",") ? commit(v) : setDraft(v); }}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(draft); }
          else if (e.key === "Backspace" && !draft && tags.length) onChange(tags.slice(0, -1));
        }}
        onBlur={() => draft.trim() && commit(draft)} />
    </div>
  );
}

function FieldInput({ field, value, onChange, options }) {
  const base =
    "w-full h-9 px-2 border border-border-control bg-white text-[13.5px] rounded-[8px] focus:border-ink-primary focus:ring-2 focus:ring-equipr-yellow/40 focus:outline-none focus:border-equipr-black";
  if (field.type === "select") {
    const opts = options || field.options || [];
    return (
      <select className={base} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        data-testid={`field-${field.key}`}>
        <option value="">— Select —</option>
        {opts.map((o) => {
          const val = o && typeof o === "object" ? o.value : o;
          const lbl = o && typeof o === "object" ? o.label : o;
          return <option key={val} value={val}>{lbl}</option>;
        })}
      </select>
    );
  }
  if (field.type === "textarea") {
    return (
      <textarea className={base + " h-20 py-1"} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        data-testid={`field-${field.key}`} />
    );
  }
  if (field.type === "checkbox") {
    return (
      <label className="flex items-center gap-2 h-9 cursor-pointer">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)}
          data-testid={`field-${field.key}`} className="h-4 w-4 accent-equipr-yellow" />
        <span className="text-sm text-gray-700">{field.checkboxLabel || "Yes"}</span>
      </label>
    );
  }
  if (field.type === "tags") {
    return <TagsInput value={value} onChange={onChange} testid={`field-${field.key}`} />;
  }
  return (
    <input type={field.type || "text"} className={base} value={value ?? ""}
      onChange={(e) => onChange(field.type === "number" ? e.target.value : e.target.value)}
      data-testid={`field-${field.key}`} />
  );
}

export default function CrudPage({ title, subtitle, endpoint, columns, fields, dynamicOptions = {}, rowActions, extraTabs = [], singular, headerActions = null }) {
  const { isWriter } = useAuth();
  const PAGE_SIZE = 25;
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [dialogTab, setDialogTab] = useState("details");
  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);
  const pageRef = useRef(1);
  const busyRef = useRef(false);
  const reqRef = useRef(0);
  const searchRef = useRef("");
  const [sortKey, setSortKey] = useState("");
  const [sortDir, setSortDir] = useState("asc");
  const sortRef = useRef({ key: "", dir: "asc" });

  const toggleSort = (key) => {
    if (key === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const fetchPage = useCallback(async (pageNum, replace) => {
    if (!replace && busyRef.current) return;         // guard only the append path
    const seq = ++reqRef.current;                    // latest request wins
    if (replace) setLoading(true); else { busyRef.current = true; setLoadingMore(true); }
    try {
      const { data } = await api.get(endpoint, { params: {
        q: searchRef.current || undefined, page: pageNum, limit: PAGE_SIZE,
        sort: sortRef.current.key || undefined, direction: sortRef.current.dir } });
      if (seq !== reqRef.current) return;             // a newer request superseded this one
      const items = Array.isArray(data) ? data : (data.items || []);
      const more = Array.isArray(data) ? false : !!data.has_more;
      const tot = Array.isArray(data) ? items.length : (data.total ?? items.length);
      setRows((prev) => (replace ? items : [...prev, ...items]));
      setTotal(tot);
      setHasMore(more);
      pageRef.current = pageNum;
    } catch (e) {
      if (seq === reqRef.current) setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      if (!replace) busyRef.current = false;
      if (seq === reqRef.current) { setLoading(false); setLoadingMore(false); }
    }
  }, [endpoint]);

  const reload = useCallback(() => { pageRef.current = 1; fetchPage(1, true); }, [fetchPage]);

  // Initial load, endpoint switch, debounced search, and sort change — always resets to page 1.
  useEffect(() => {
    const t = setTimeout(() => {
      searchRef.current = search;
      sortRef.current = { key: sortKey, dir: sortDir };
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
      pageRef.current = 1;
      fetchPage(1, true);
    }, search ? 350 : 0);
    return () => clearTimeout(t);
  }, [endpoint, search, sortKey, sortDir, fetchPage]);

  // Infinite scroll: load the next page when the sentinel enters view.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !busyRef.current) fetchPage(pageRef.current + 1, false);
    }, { root: scrollRef.current, rootMargin: "250px" });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, fetchPage, rows.length]);

  const openAdd = () => {
    const init = {};
    fields.forEach((f) => { init[f.key] = f.type === "tags" ? [] : f.default ?? ""; });
    setForm(init);
    setEditing(null);
    setError("");
    setDialogTab("details");
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    const init = { ...row };
    fields.forEach((f) => {
      if (f.default !== undefined && (init[f.key] === undefined || init[f.key] === null || init[f.key] === ""))
        init[f.key] = f.default;
    });
    setForm(init);
    setEditing(row);
    setError("");
    setDialogTab("details");
    setDialogOpen(true);
  };

  const save = async () => {
    setError("");
    const payload = { ...form };
    fields.forEach((f) => {
      if (f.type === "number")
        payload[f.key] = payload[f.key] === "" || payload[f.key] == null ? null : Number(payload[f.key]);
    });
    try {
      if (editing) await api.put(`${endpoint}/${editing.id}`, payload);
      else await api.post(endpoint, payload);
      setDialogOpen(false);
      reload();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete this ${title.slice(0, -1).toLowerCase()}?`)) return;
    try {
      await api.delete(`${endpoint}/${row.id}`);
      reload();
    } catch (e) {
      alert(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="h-full flex flex-col bg-surface-page p-4">
      <div className="flex-1 min-h-0 flex flex-col bg-surface-card border border-border rounded-[12px] shadow-card overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-divider-inner shrink-0">
        <div>
          <h1 className="font-heading text-[17px] font-bold tracking-tight text-ink-primary">{title}</h1>
          {subtitle && <p className="text-[12px] text-ink-tertiary">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-2">
          {headerActions}
          <div className="relative">
            <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-tertiary" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…"
              data-testid="crud-search-input"
              className="w-72 h-[42px] pl-9 pr-3 bg-white border-[1.5px] border-ink-primary rounded-[7px] text-body focus:outline-none focus:shadow-[0_0_0_3px_rgba(255,209,8,0.35)]" />
          </div>
          {isWriter && (
            <button onClick={openAdd} data-testid={`add-${title.toLowerCase()}-button`}
              className="h-9 px-3 bg-equipr-yellow text-equipr-black font-bold text-[12px] uppercase tracking-[0.04em] rounded-[7px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px inline-flex items-center gap-1.5">
              <Plus size={15} weight="bold" /> New
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-auto" ref={scrollRef}>
        <table className="w-full text-cell">
          <thead className="bg-surface-sunken sticky top-0 z-10">
            <tr>
              {columns.map((c) => {
                const sortable = c.sortable !== false;
                const active = sortKey === c.key;
                return (
                  <th key={c.key}
                    onClick={sortable ? () => toggleSort(c.key) : undefined}
                    className={`text-left font-semibold text-colhead uppercase text-ink-tertiary px-4 py-2 whitespace-nowrap select-none ${sortable ? "cursor-pointer hover:text-ink-primary" : ""}`}
                    data-testid={sortable ? `sort-${c.key}` : undefined}>
                    <span className="inline-flex items-center gap-1">
                      {c.label}
                      {sortable && (active
                        ? (sortDir === "asc" ? <CaretUp size={11} weight="bold" className="text-ink-primary" /> : <CaretDown size={11} weight="bold" className="text-ink-primary" />)
                        : <CaretUpDown size={11} className="text-ink-muted/60" />)}
                    </span>
                  </th>
                );
              })}
              {rowActions && <th className="w-16 px-4 py-2 text-right font-semibold text-colhead uppercase text-ink-tertiary"></th>}
              {isWriter && <th className="w-20 px-4 py-2"></th>}
            </tr>
          </thead>
          <tbody data-testid="crud-table-body">
            {loading ? (
              <SkeletonRows cols={columns.length + (rowActions ? 1 : 0) + (isWriter ? 1 : 0)} />
            ) : rows.length === 0 ? (
              <tr><td colSpan={columns.length + 2} className="px-4 py-8 text-center text-ink-tertiary" data-testid="crud-empty">{search ? "No records match your search." : "No records yet."}</td></tr>
            ) : rows.map((row) => (
              <tr key={row.id} onClick={() => isWriter && openEdit(row)}
                className={`border-b border-divider-row hover:bg-surface-panel ${isWriter ? "cursor-pointer" : ""}`} data-testid={`row-${row.id}`}>
                {columns.map((c) => (
                  <td key={c.key} className="px-4 py-1.5 whitespace-nowrap text-equipr-black">
                    {c.render ? c.render(row[c.key], row) : (row[c.key] ?? "—")}
                  </td>
                ))}
                {rowActions && (
                  <td className="px-4 py-1.5 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>{rowActions(row)}</td>
                )}
                {isWriter && (
                  <td className="px-4 py-1.5 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => openEdit(row)} className="text-gray-500 hover:text-equipr-black mr-3" data-testid={`edit-${row.id}`}>
                      <PencilSimple size={16} />
                    </button>
                    <button onClick={() => remove(row)} className="text-gray-500 hover:text-red-600" data-testid={`delete-${row.id}`}>
                      <Trash size={16} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {!loading && hasMore && (
              <tr ref={sentinelRef} data-testid="crud-scroll-sentinel">
                <td colSpan={columns.length + 2} className="px-4 py-3 text-center text-meta text-ink-tertiary">
                  {loadingMore ? "Loading more…" : "Scroll for more"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between px-5 py-2 border-t border-divider-inner bg-surface-panel text-meta text-ink-tertiary shrink-0">
        <span data-testid="crud-footer">{loading ? "Loading…" : `Showing ${rows.length}${(total > rows.length || hasMore) ? ` of ${total}` : ""} ${title.toLowerCase()}${search ? " (filtered)" : ""}`}</span>
      </div>
      </div>

      {dialogOpen && (
        <div className="fixed inset-0 z-50 bg-[rgba(26,26,26,0.34)] flex justify-end eq-sheet-overlay" onClick={() => setDialogOpen(false)}>
          <div className="bg-surface-panel shadow-drawer border-l border-border-warm w-full max-w-[800px] h-full flex flex-col eq-sheet" onClick={(e) => e.stopPropagation()} data-testid="crud-dialog">
            <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-divider-inner shrink-0">
              <div>
                <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-ink-muted">{editing ? "Edit" : "New"} {singular || title.slice(0, -1)}</div>
                <h3 className="font-bold text-[20px] text-ink-primary leading-tight mt-0.5">{editing ? (editing.name || editing.project_name || editing.asset_no || singular || "Record") : `New ${singular || title.slice(0, -1)}`}</h3>
              </div>
              <button onClick={() => setDialogOpen(false)} className="w-9 h-9 flex items-center justify-center rounded-[9px] border border-border-control text-ink-secondary hover:bg-surface-panel"><X size={16} weight="bold" /></button>
            </div>
            {extraTabs.length > 0 && (
              <div className="flex items-center gap-1 px-4 py-2 border-b border-divider-inner shrink-0 bg-surface-inset/40">
                {[{ key: "details", label: "Details" }, ...extraTabs].map((t) => (
                  <button key={t.key} onClick={() => setDialogTab(t.key)} data-testid={`crud-tab-${t.key}`}
                    className={`px-3 h-8 rounded-[999px] text-[12px] font-bold uppercase tracking-[0.04em] ${dialogTab === t.key ? "bg-equipr-yellow text-black" : "text-ink-secondary hover:text-ink-primary"}`}>{t.label}</button>
                ))}
              </div>
            )}
            {dialogTab === "details" ? (
              <div className="p-5 flex-1 overflow-auto grid grid-cols-2 gap-x-3 gap-y-4 content-start auto-rows-min">
                {error && <div className="col-span-2 border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}
                {fields.map((f) => (
                  <div key={f.key} className={f.full ? "col-span-2" : ""}>
                    <label className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-ink-muted mb-1.5 block">{f.label}{f.required && " *"}</label>
                    <FieldInput field={f} value={form[f.key]} options={dynamicOptions[f.key]}
                      onChange={(v) => setForm((s) => ({ ...s, [f.key]: v }))} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-5 flex-1 overflow-auto">
                {extraTabs.find((t) => t.key === dialogTab)?.render({ form, setForm, editing })}
              </div>
            )}
            <div className="flex justify-end gap-2 px-5 py-3 bg-surface-panel border-t border-divider-inner shrink-0">
              <button onClick={() => setDialogOpen(false)} className="h-9 px-4 border border-border-control bg-white text-equipr-black text-[12px] uppercase tracking-[0.04em] font-semibold rounded-[7px] hover:bg-surface-panel active:bg-surface-pressed">Cancel</button>
              <button onClick={save} data-testid="crud-save-button" className="h-9 px-4 bg-equipr-yellow text-equipr-black text-[12px] uppercase tracking-[0.04em] font-bold rounded-[7px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
